/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2021, 2022
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import { createAsyncThunk } from '@reduxjs/toolkit';
import _ from 'lodash';

import { addErrorMessage } from '@ibm-watson/shared/modules/ia/store/messages/actions';
import { deleteSkill } from '@ibm-watson/shared/modules/ia/store/skills/actions';
import type {
  DiscoveryField,
  DiscoveryFieldExamples,
  GetDiscoveryInstancesResponse
} from '../../../ts/discovery';
import type { CreateAsyncThunk, DiscoveryInstance } from '../../../ts/state';
import {
  selectAllEnvironments,
  selectSearchSkillIdByEnvironment
} from '../../ia/store/environments/selectors';
import { isCacheExpired, STATUS } from '../../ia/store/helper';
import * as discoveryApi from '../api';
import { selectInstanceById } from './selectors';

export const listInstances = createAsyncThunk<
  GetDiscoveryInstancesResponse,
  never,
  CreateAsyncThunk
>(
  'discovery/listInstances',
  async (_, { dispatch }) => {
    try {
      const response = await discoveryApi.getDiscoveryInstances();
      const instances = response.instances ?? [];
      const unavailableInstances = response.unavailableInstances ?? [];
      return { instances, unavailableInstances };
    } catch (error) {
      dispatch(addErrorMessage(error));
      throw error;
    }
  },
  {
    // this condition parameter passed to createAsyncThunk causes the call to cancel
    //  (with no payload processing) if we already have an earlier call in progress
    condition: (_, { getState }) => {
      const { listInstanceStatus } = getState()?.discovery;
      if (listInstanceStatus === STATUS.LOADING) {
        return false;
      }
    }
  }
);

export const listInstancesIfRequired = () => {
  return (dispatch, getState) => {
    const { listInstanceFetched } = getState()?.discovery;
    // Avoid requests if data is not stale
    if (!isCacheExpired(listInstanceFetched)) {
      return Promise.resolve();
    }
    return dispatch(listInstances());
  };
};

export const listProjects = createAsyncThunk<
  DiscoveryInstance,
  string,
  CreateAsyncThunk
>('discovery/listProjects', async (instanceId, { dispatch, getState }) => {
  try {
    // returns an array of project objects
    const response =
      await discoveryApi.getDiscoveryV2InstanceProjectsCollections(instanceId);
    const instance = selectInstanceById(getState(), instanceId);
    const updateInstance = _.cloneDeep(instance);
    updateInstance.projects = response;
    return updateInstance;
  } catch (error) {
    dispatch(addErrorMessage(error));
    throw error;
  }
});

export const listProjectsIfRequired = (instanceId: string) => {
  return (dispatch, getState) => {
    const instance = selectInstanceById(getState(), instanceId);
    // Avoid requests if data is not stale
    if (!isCacheExpired(instance?.listProjectFetched)) {
      return Promise.resolve();
    }
    return dispatch(listProjects(instanceId));
  };
};

export const deleteSearchAcrossAllEnvironments = createAsyncThunk<
  void,
  never,
  CreateAsyncThunk
>('discovery/deleteAcrossEnvironments', async (__, { dispatch, getState }) => {
  try {
    //this is called from an integration card, so envs & skills are already listed
    const environments = selectAllEnvironments(getState());
    // sort the environments so `draft` is last
    let sortedEnvironments = _.cloneDeep(environments);
    sortedEnvironments = sortedEnvironments.sort(a =>
      a.environment === 'draft' ? 1 : -1
    );
    const promises = [];
    sortedEnvironments.forEach(env => {
      const ssId = selectSearchSkillIdByEnvironment(
        getState(),
        env.environment_id
      );
      if (ssId && ssId !== '') {
        promises.push(dispatch(deleteSkill(ssId)));
      }
    });
    await Promise.all(promises);
  } catch (error) {
    dispatch(addErrorMessage(error));
    throw error;
  }
});

export const listFields = createAsyncThunk<
  { fields: DiscoveryField[] },
  { instanceId: string; projectId: string },
  CreateAsyncThunk
>('discovery/listFields', async ({ instanceId, projectId }, { dispatch }) => {
  try {
    // returns an array of fields
    const response = await discoveryApi.getDiscoveryV2ProjectFields(
      projectId,
      instanceId
    );
    return { fields: response };
  } catch (error) {
    dispatch(addErrorMessage(error));
    throw error;
  }
});

export const listFieldExamples = createAsyncThunk<
  { fieldExamples: DiscoveryFieldExamples },
  { instanceId: string; projectId: string },
  CreateAsyncThunk
>(
  'discovery/listFieldExamples',
  async ({ instanceId, projectId }, { dispatch }) => {
    try {
      // returns an array of field examples
      const response = await discoveryApi.getDiscoveryProjectFieldExamples(
        instanceId,
        projectId
      );
      return { fieldExamples: response };
    } catch (error) {
      dispatch(addErrorMessage(error));
      throw error;
    }
  }
);
